import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { PhotoCamera, Save } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const colorSchemes = [
  { id: 'blue', name: 'Ocean Blue', primary: '#1976d2', secondary: '#dc004e' },
  { id: 'green', name: 'Forest Green', primary: '#2e7d32', secondary: '#f57c00' },
  { id: 'purple', name: 'Royal Purple', primary: '#7b1fa2', secondary: '#e91e63' },
  { id: 'teal', name: 'Teal Breeze', primary: '#00796b', secondary: '#ff5722' },
  { id: 'orange', name: 'Sunset Orange', primary: '#f57c00', secondary: '#3f51b5' },
  { id: 'indigo', name: 'Deep Indigo', primary: '#303f9f', secondary: '#ff9800' },
];

const UserSettingsPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [selectedColorScheme, setSelectedColorScheme] = useState(user?.color_scheme || 'blue');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update local state when user context changes
  useEffect(() => {
    if (user?.color_scheme) {
      setSelectedColorScheme(user.color_scheme);
    }
  }, [user?.color_scheme]);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image must be smaller than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('color_scheme', selectedColorScheme);
      
      if (profileImage) {
        formData.append('profile_picture', profileImage);
      }
      
      const response = await api.put('/api/users/preferences', formData);
      
      if (response.success) {
        updateUser(response.data.user);
        setSuccess(true);
        setProfileImage(null);
        setProfileImagePreview(null);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const currentScheme = colorSchemes.find(scheme => scheme.id === selectedColorScheme);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        User Settings
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Photo Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Profile Photo
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={profileImagePreview || user?.custom_profile_picture || user?.profile_picture}
                  sx={{ width: 120, height: 120 }}
                />
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                >
                  Change Photo
                </Button>
                
                {profileImage && (
                  <Typography variant="body2" color="text.secondary">
                    New photo selected: {profileImage.name}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Color Scheme Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Color Theme
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Color Scheme</InputLabel>
                <Select
                  value={selectedColorScheme}
                  label="Color Scheme"
                  onChange={(e) => setSelectedColorScheme(e.target.value)}
                  disabled={loading}
                >
                  {colorSchemes.map((scheme) => (
                    <MenuItem key={scheme.id} value={scheme.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${scheme.primary}, ${scheme.secondary})`,
                          }}
                        />
                        {scheme.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Color Preview */}
              {currentScheme && (
                <Paper 
                  sx={{ 
                    p: 2, 
                    background: `linear-gradient(135deg, ${currentScheme.primary}, ${currentScheme.secondary})`,
                    color: 'white',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {currentScheme.name} Preview
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    This is how your theme will look
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Account Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {user?.name}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {user?.email}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Provider
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, textTransform: 'capitalize' }}>
                    {user?.provider}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSaveSettings}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserSettingsPage;
